import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import { CircularProgress, Typography, Button, Box, FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { getAllV2Gamefacts, getAllV2GamefactsStats, getOngletComponent, getPageInfos, getNavigationsComponent, getV2LiveHeaderImage } from '../../../api/index.js';
import SearchFilterDTO from '../../DTO/SearchFilterDTO.js';
import ClassementDTO from '../../DTO/ClassementDTO.js';
import { useURLSearchParams } from '../../hooks/useUrlParams.js';
import Footer from '../../components/footer/Footer.js';

const ClassementResolver = ({viewIdName = "classement"}) => {

  //Display specific iframe options
  const [queryParams] = useSearchParams();
  const redirectUrl = queryParams.get('redirect-url');
  const displayText = queryParams.get('display-text');
  let displayPosition = queryParams.get('display-pos');
  const DSASLogo = queryParams.get('dsas-logo');
  const DSASLogoSize=queryParams.get('display-logo-sz');
  const DSASLogoDisplay=queryParams.get('display-logo-pos');

  //Force top display of redirect button by default if not set
  if (!displayPosition)
    displayPosition = 'top'


  const castToBoolean = (value, defaultValue) => {
    if (value === null || value === undefined) {
      return defaultValue;
    }
    return value.toLowerCase() === 'true';
  };

  const showDSASLogo = castToBoolean(DSASLogo, true);

  const { id } = useParams();

  const [headerImage, setHeaderImage] = useState(null);
  const [pageInfosByCompetition, setPageInfosByCompetition] = useState()
  const [live, setLive] = useState()

  const { searchParams, setParam, deleteParam } = useURLSearchParams();

  useEffect(() => {
    // On doit récupérer la liste des competitions du live
    const fetchData = async () => {
      try {
        const response = await getPageInfos(id, viewIdName);
        const { data } = response

        setPageInfosByCompetition(data.pageInfosByCompetition);
        setLive(data.live)
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();

  }, [])

  useEffect(() => {
    let isMounted = true;

    const fetchHeaderImage = async () => {
      try {
        const res = await getV2LiveHeaderImage(id, "default");

        if (!isMounted) return;

        setHeaderImage(res?.data || null);
      } catch (e) {
        console.error("Erreur récupération bandeau live:", e);

        if (!isMounted) return;

        setHeaderImage(null);
      }
    };

    if (id) {
      fetchHeaderImage();
    }

    return () => {
      isMounted = false;
    };
  }, [id]);
  
  if (!pageInfosByCompetition || !live) {
    return <CircularProgress/>
  }

  if (!pageInfosByCompetition[0] || pageInfosByCompetition[0]===null) {
    return 'Aucune page disponible'
  }


  return <Box sx={{textAlign : 'center'}}>
   
   {/* REDIRECT BUTTON - TOP POSITION */}
    {(redirectUrl && displayText && displayPosition==='top') && (
        <Button
        variant="contained"
        style={{ backgroundColor: '#4A5168', color: '#ffffff', fontSize:'0.7em', padding:'5px' }}
        onClick={() => window.top.location.href = redirectUrl}
      >
        {displayText}
      </Button>
      )}

      {headerImage?.headerImageUrl && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            pt: 2,
            pb: 2,
          }}
        >
          <Box
            component="img"
            src={headerImage.headerImageUrl}
            alt={headerImage.headerImageAlt || ""}
             sx={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </Box>
      )}

    {pageInfosByCompetition && pageInfosByCompetition.map((pageInfos, idx) => 
            <ViewsResolver 
              key={idx} 
              views={pageInfos.views} 
              competition={pageInfos.competition} 
              pageIdName={viewIdName} 
              searchParams={searchParams} 
              setParam={setParam} 
              deleteParam={deleteParam}
              showDSASLogo={showDSASLogo}
              DSASLogoSize={DSASLogoSize} 
              DSASLogoDisplay={DSASLogoDisplay}
              redirectUrl={redirectUrl}
              displayText={displayText}
              displayPosition={displayPosition}
              />
    )}
  </Box>
  

}

const ViewsResolver = ({ views, competition, pageIdName, searchParams, setParam, deleteParam, showDSASLogo,  DSASLogoSize, DSASLogoDisplay, redirectUrl, displayText, displayPosition}) => {

  
  const { id } = useParams();

  const paramView = useMemo(() => searchParams.get("view"), [searchParams]);
  const paramEtape = useMemo(() => searchParams.get("etape"), [searchParams]);

  const [activeView, setActiveView] = useState()

  const [activeOnglet, setActiveOnglet] = useState()

  const [navigationsVariables, setNavigationsVariables] = useState(paramEtape ? {etape : paramEtape} : {})

  const [dataTab, setDataTab] = useState()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()


  const [page, setPage] = useState(1);     // 1-based pour l’API
  const [limit, setLimit] = useState(10);  // sera synchronisé avec lineVisible ensuite au besoin

  // const skipNextPageFetchRef = useRef(false);

  const fetchData = async (tab, opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;

    setLoading(true);
    setError(undefined);
    setDataTab(undefined);

    try {
      const { idName } = tab;
      const response = await resolveOnglet(idName, {
        ...navigationsVariables,
        page: nextPage,
        limit: nextLimit,
      });
      const { data } = response;
      setDataTab(data);
    } catch (error) {
      setError(error);
    }
  };

  useEffect(() => {
    if (dataTab || error) setLoading(false);
  }, [dataTab, error]);

  // 1) Quand onglet OU filtres changent => reset page 1 + fetch
  useEffect(() => {
    if (!activeView) return;

    if (activeView.navigations && !activeView.navigations.every(n => navigationsVariables[n.idName]))
      return;

    if (!activeView.navigations && Object.keys(navigationsVariables).length > 0)
      return;

    if (!activeOnglet) return;

    // skipNextPageFetchRef.current = true;
    setPage(1);
    fetchData(activeOnglet, { page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnglet, navigationsVariables]);

  // 2) Quand page OU limit changent => fetch (sans reset)
  // (si limit change, on choisit de revenir page 1 via handler côté UI)
  useEffect(() => {
    console.log('page/limit changed', page, limit);
    if (!activeOnglet) return;
    fetchData(activeOnglet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);


  useEffect(() => {
    if(paramView){
      let newActiveView = views.find(v => v.idName === paramView)
      setActiveView(newActiveView)
    }
    else{
      setActiveView(views[0])
    }

  }, [])

  useEffect(() => {
    if(activeView && activeView.idName !== paramView){
      setParam('view', activeView.idName)
      setNavigationsVariables({})
    }
  }, [activeView])

  useEffect(() => {

    if(Object.keys(navigationsVariables).length === 0){
      deleteParam('etape')
    }

    Object.keys(navigationsVariables).map(key => {
      setParam([key], navigationsVariables[key])
    })
    
  }, [navigationsVariables])

  const resolveNavigation = useCallback(async (idNameNavigation) => {
    return await getNavigationsComponent(id, competition._id, competition.idNameConfig, pageIdName, activeView.idName, idNameNavigation)
  }, [activeView, competition])

  const resolveOnglet = useCallback(async (idNameOnglet, external) => {
    return await getOngletComponent(id, competition._id, competition.idNameConfig, pageIdName, activeView.idName, idNameOnglet, external)
  }, [activeView, competition])

  if(!activeView)
    return <CircularProgress/>

  return (
    <Box sx={{m : 1}}>
      <Box sx={{textAlign : 'center', mb : 1}}>
        {views.length > 1 && views.map(view => <Button variant={activeView===view ? 'contained' : 'text'} key={view.idName} onClick={() => setActiveView(view)}>{view.label}</Button>)}
        {views.length === 1 && views.map(view => <Typography  sx={{fontSize : '1.1em', fontWeight : 'bold', textTransform : 'uppercase', color :  "#4A5168"}} key={view.idName}>{view.label}</Typography>)}
      </Box>

       {activeView.navigations && <NavigationResolver 
                                    activeView={activeView} 
                                    resolveNavigation={resolveNavigation} 

                                    navigationsVariables={navigationsVariables}
                                    setNavigationsVariables={setNavigationsVariables}

                                    searchParams={searchParams} setParam={setParam} deleteParam={deleteParam}
                                  />
        }

      <OngletResolver onglets={activeView.onglets} resolveOnglet={resolveOnglet} 
                                    navigationsVariables={navigationsVariables}
                                    setNavigationsVariables={setNavigationsVariables}

                                    searchParams={searchParams} setParam={setParam} deleteParam={deleteParam}
                                    activeOnglet={activeOnglet} setActiveOnglet={setActiveOnglet}
                                    dataTab={dataTab} loading={loading} error={error}
                                    showDSASLogo={showDSASLogo}
                                    DSASLogoSize={DSASLogoSize} 
                                    DSASLogoDisplay={DSASLogoDisplay}
                                    redirectUrl={redirectUrl}
                                    displayText={displayText}
                                    displayPosition={displayPosition}
                                    page={page}
                                    limit={limit}
                                    setPage={setPage}
                                    setLimit={setLimit}
                                    />
    </Box>
    
  )
}

const NavigationResolver = ({activeView, resolveNavigation, navigationsVariables, setNavigationsVariables, searchParams, setParam, deleteParam}) => {

  const [resolvedNavigations, setResolvedNavigations] = useState([])

  useEffect(async () => {

    if(!activeView.navigations)
      return

    const result = []
    const navVariables = {...navigationsVariables}

    for(var navigation of activeView.navigations){
    // Appeler le serveur qu'il me renvoie la liste des navigations
      const response = await resolveNavigation(navigation.idName)
      const { data } = response
      result.push(data)

      Object.keys(data).forEach(key => {
        const paramValue = searchParams.get(key);
        if (paramValue) {
          navVariables[key] = paramValue;
          return;
        }

        const items = data[key];
        const now = new Date();

        const withDate = items.filter(item => item.date);
        const noDate = items.filter(item => !item.date);

        const past = withDate
          .filter(item => new Date(item.date) <= now)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        const future = withDate
          .filter(item => new Date(item.date) > now)
          .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

        navVariables[key] =
          past?.id ?? future?.id ?? noDate[0]?.id ?? items[0]?.id ?? null;
      });
    }

    setResolvedNavigations(result)
    setNavigationsVariables(navVariables)

  }, [, activeView.navigations])

  const getLibelle = (idName) => {
    const navigationFound = activeView.navigations.find(n => n.idName === idName)

    if(navigationFound)
        return navigationFound.libelle.charAt(0).toUpperCase() + navigationFound.libelle.slice(1);

    return idName.charAt(0).toUpperCase() + idName.slice(1);
  }

  if(!resolvedNavigations)
    return <CircularProgress/>

  return (
    <Box sx={{mt:2}}>
      {resolvedNavigations.map(navigation => Object.keys(navigation).map((key, index) => (
          <SelectInput key={index} label={getLibelle(key)} options={navigation[key]} selected={navigationsVariables[key]} onChange={(event) => setNavigationsVariables(n => ({...n, [key] : event.target.value}))} />
          ))
        )}
    </Box>
  )

}

const SelectInput = ({ label, options, selected = '', onChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <FormControl fullWidth margin="normal">
      <InputLabel>{label}</InputLabel>
      <Select value={selected} onChange={onChange} label={label}>
        {options.map((option, index) => (
          <MenuItem key={index} value={option.id}>
            <span
              style={{
                fontSize: '1rem',
                display: 'inline-block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {option.prefix}
              <span
                style={{
                  fontSize: isMobile ? '0.75rem' : 'inherit',
                  marginLeft: 4,
                }}
              >
                : {option.label}
              </span>
            </span>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const OngletResolver = ({ onglets, 
                          resolveOnglet, 
                          navigationsVariables, 
                          setNavigationsVariables, 
                          searchParams, 
                          setParam, 
                          deleteParam, 
                          activeOnglet, 
                          setActiveOnglet, 
                          dataTab, 
                          loading, 
                          error, 
                          showDSASLogo, 
                          DSASLogoSize,
                          DSASLogoDisplay,
                          redirectUrl, 
                          displayText, 
                          displayPosition,
                          page,
                          limit,
                          setPage,
                          setLimit
                        }) => {

  const { id } = useParams();

  const paramTab = useMemo(() => searchParams.get("tab"), [searchParams]);

  

  useEffect(() => {
    if(paramTab){
      let newActiveOnglet = onglets.find(o => o.idName === paramTab)
      setActiveOnglet(newActiveOnglet ?? onglets[0])
    }
    else
      setActiveOnglet(onglets[0])

  }, [, onglets])

  

  useEffect(() => {
    if(activeOnglet && activeOnglet.idName !== paramTab){
      setParam('tab', activeOnglet.idName)
    }
  }, [activeOnglet])


  return <React.Fragment>
    <Box sx={{textAlign : 'center'}}>
      { onglets.length > 1 && onglets.map((onglet, idx) => <Button 
                sx={{borderRadius : '20px', mr : 1}}
                variant={activeOnglet===onglet ? 'outlined' : 'text'} 
                key={onglet.idName}
                onClick={() => {
                  setActiveOnglet(onglet);
                  setPage?.(1);
                }}
                disabled={loading}
                >
                  <Typography fontSize={'0.9em'} sx={{display : 'flex', alignItems : 'center'}}><span style={{color : onglet.color, fontSize : "2em", lineHeight : '0.1em', paddingRight : '0.2em'}}>&#x2022;</span> {onglet.label}</Typography>  
                </Button>)}
    </Box>

      {loading && <CircularProgress/>}
      {(error) && <Typography pt={"50px"}>Erreur lors du chargement des résultats.</Typography>}
      {dataTab && (
        <div style={{ padding: 0, margin: 0 }}>
          <Outlet
            context={{
              dataComponent: dataTab,
              pager: { page, limit },
              setPage,
              setLimit,
            }}
          />
        </div>
      )}

      {(redirectUrl && displayText && displayPosition==='bottom') && (
        <Button
        variant="contained"
        style={{
          position: 'absolute',
          backgroundColor: '#4A5168',
          color: '#ffffff',
          fontSize: '0.7em',
          padding: '5px',
          left: '50%',  // Centre horizontalement par rapport au conteneur parent
          transform: 'translateX(-50%)',  // Ajuste pour un centrage parfait
          bottom: '10px' // Ajuste la position verticale si nécessaire
        }}
        onClick={() => window.top.location.href = redirectUrl}
      >
        {displayText}
      </Button>
      )}

      <Footer isDSAS={showDSASLogo} DSASLogoSize={DSASLogoSize} DSASLogoDisplay={DSASLogoDisplay} />

    </React.Fragment>;
}

export default ClassementResolver